import { useEffect, useState } from "react";

import {
  attachDesktopSshHostKeyPrompts,
  resolveSshHostKeyPrompt,
  subscribeSshHostKeyPrompts,
  type SshHostKeyPromptRequest,
} from "../connection/sshHostKeyPrompt";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";

function formatRemaining(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * Asks whether a machine being connected to for the first time is the one the person meant.
 *
 * One at a time, oldest first, for the same reason the password dialog does it: two stacked
 * dialogs cannot be told apart, and the person would not know which destination they were
 * answering.
 */
export function SshHostKeyPromptDialog() {
  const [requests, setRequests] = useState<ReadonlyArray<SshHostKeyPromptRequest>>([]);
  useEffect(() => subscribeSshHostKeyPrompts(setRequests), []);
  useEffect(() => attachDesktopSshHostKeyPrompts(), []);
  const current = requests[0] ?? null;
  if (current === null) return null;
  return <ActiveSshHostKeyPrompt key={current.requestId} request={current} />;
}

function ActiveSshHostKeyPrompt({ request }: { readonly request: SshHostKeyPromptRequest }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, []);

  const remainingSeconds = Math.max(0, Math.floor((request.expiresAtMs - now) / 1000));
  useEffect(() => {
    if (remainingSeconds > 0) return;
    resolveSshHostKeyPrompt(request.requestId, false);
  }, [remainingSeconds, request.requestId]);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        // Dismissing is declining. Nothing is recorded and the connection stops.
        if (!open) resolveSshHostKeyPrompt(request.requestId, false);
      }}
    >
      <DialogPopup className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Is this {request.destination}?</DialogTitle>
          <DialogDescription>
            This machine has not been connected to from here before, so there is nothing to check
            its identity against yet. Accepting records this key, and every later connection is
            refused if it does not match.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <p className="text-xs text-muted-foreground">{request.keyType} key fingerprint</p>
          <p className="pt-1 font-mono text-sm break-all">{request.fingerprint}</p>
          <p className="pt-3 text-xs text-muted-foreground">
            Compare it with what the machine itself reports, by running{" "}
            <code className="font-mono">ssh-keygen -lf /etc/ssh/ssh_host_{"<type>"}_key.pub</code>{" "}
            on it. Accept only if they match.
          </p>
          <p aria-live="polite" className="pt-3 text-xs text-muted-foreground">
            Expires in {formatRemaining(remainingSeconds)}
          </p>
        </DialogPanel>
        <DialogFooter>
          <Button variant="ghost" onClick={() => resolveSshHostKeyPrompt(request.requestId, false)}>
            Don&rsquo;t connect
          </Button>
          <Button onClick={() => resolveSshHostKeyPrompt(request.requestId, true)}>
            Accept and connect
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
